import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Clock, User, Phone, Mail, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { useUserProfile } from '@/hooks/useUserProfile';

interface AppointmentBookingProps {
  propertyId: string;
  propertyTitle: string;
  propertyLocation: string;
  isEmbedded?: boolean;
}

export const AppointmentBooking: React.FC<AppointmentBookingProps> = ({
  propertyId,
  propertyTitle,
  propertyLocation,
  isEmbedded = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { profile } = useUserProfile();
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    preferredDate: '',
    preferredTime: '',
    notes: '',
  });

  // Auto-fill from profile
  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        clientName: prev.clientName || profile.full_name,
        clientEmail: prev.clientEmail || profile.email,
        clientPhone: prev.clientPhone || profile.phone,
      }));
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Combine date and time
      const scheduledAt = new Date(`${formData.preferredDate}T${formData.preferredTime}`);

      const { error } = await supabase
        .from('appointments')
        .insert({
          property_id: propertyId,
          client_name: formData.clientName,
          client_email: formData.clientEmail,
          client_phone: formData.clientPhone || null,
          scheduled_at: scheduledAt.toISOString(),
          notes: formData.notes || null,
          status: 'scheduled',
        });

      if (error) throw error;

      toast({
        title: "Appointment Requested",
        description: "Your appointment request has been submitted. We'll contact you soon to confirm.",
      });

      // SMS confirmation is handled server-side by admin/staff workflows

      setIsOpen(false);
      setFormData({
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        preferredDate: '',
        preferredTime: '',
        notes: '',
      });
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast({
        title: "Error",
        description: "Failed to submit appointment request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  const formContent = (
    <>
      <div className="mb-4 p-3 bg-muted rounded-lg">
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="font-medium">{propertyTitle}</p>
            <p className="text-muted-foreground">{propertyLocation}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="clientName">Full Name *</Label>
            <Input
              id="clientName"
              type="text"
              value={formData.clientName}
              onChange={(e) => handleInputChange('clientName', e.target.value)}
              required
              placeholder="Your full name"
            />
          </div>
          <div>
            <Label htmlFor="clientPhone">Phone Number</Label>
            <Input
              id="clientPhone"
              type="tel"
              value={formData.clientPhone}
              onChange={(e) => handleInputChange('clientPhone', e.target.value)}
              placeholder="Your phone number"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="clientEmail">Email Address *</Label>
          <Input
            id="clientEmail"
            type="email"
            value={formData.clientEmail}
            onChange={(e) => handleInputChange('clientEmail', e.target.value)}
            required
            placeholder="your.email@example.com"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="preferredDate">Preferred Date *</Label>
            <Input
              id="preferredDate"
              type="date"
              value={formData.preferredDate}
              onChange={(e) => handleInputChange('preferredDate', e.target.value)}
              min={today}
              required
            />
          </div>
          <div>
            <Label htmlFor="preferredTime">Preferred Time *</Label>
            <Input
              id="preferredTime"
              type="time"
              value={formData.preferredTime}
              onChange={(e) => handleInputChange('preferredTime', e.target.value)}
              required
            />
          </div>
        </div>

        <div>
          <Label htmlFor="notes">Additional Notes</Label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Any specific requirements or questions..."
            rows={3}
          />
        </div>

        <div className="p-3 rounded-lg text-sm bg-primary/10 text-primary">
          <p className="font-medium mb-1">What happens next?</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>Our team will review your request</li>
            <li>We'll contact you within 24 hours to confirm</li>
            <li>You'll receive appointment details via email</li>
          </ul>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsOpen(false)}
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1"
            disabled={loading}
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>
            ) : (
              <>
                <Calendar className="h-4 w-4 mr-2" />
                Book Viewing
              </>
            )}
          </Button>
        </div>
      </form>
    </>
  );

  if (isEmbedded) {
    return formContent;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="w-full">
          <Calendar className="h-4 w-4 mr-2" />
          Schedule Viewing
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule Property Viewing</DialogTitle>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
};